import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import {
  noWhitespaceValidator,
} from '../../shared/services/validations';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AccommodationService } from '../../features/accommodation/services/accommodation.service';

@Component({
  selector: 'app-accommodation-new',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    ReactiveFormsModule,
    FormsModule,
    MatDatepickerModule,
  ],
  templateUrl: './accommodation-new.component.html',
  styleUrl: './accommodation-new.component.scss',
})
export class AccommodationNewComponent {
  newAccommodationForm: FormGroup;
  accommodationData: any;
  accommodationId: any;

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private accommodationService: AccommodationService,
  ) {}

  ngOnInit() {
    this.initForm();
    this.route.paramMap.subscribe((params) => {
      this.accommodationId = params.get('id');
      if (this.accommodationId) {
        this.getPropertyById();
      }
    });
  }

  initForm() {
    if (this.accommodationData) {      
      this.newAccommodationForm = this.fb.group({
        propertyName: [
          this.accommodationData?.propertyName.toString() || '',
          [
            Validators.required,
            Validators.maxLength(50),
            noWhitespaceValidator(),
          ],
        ],
        address: [
          this.accommodationData?.address.toString() || '',
          [Validators.required, noWhitespaceValidator()],
        ],
        annualRent: [
          this.accommodationData?.annualRent || '',
          [Validators.required, Validators.min(0.01)],
        ],
        vacancies: [
          this.accommodationData?.vacancies || '',
          [Validators.required, Validators.min(0.01)],
        ],
      });
    } else {
      this.newAccommodationForm = this.fb.group({
        propertyName: ['', [Validators.required, Validators.maxLength(50), noWhitespaceValidator()]],
        address: ['', [Validators.required, noWhitespaceValidator()]],
        annualRent: ['', [Validators.required, Validators.min(0.01)]],
        vacancies: ['', [Validators.required, Validators.min(0.01)]],
      });
    }
  }

  allowOnlyNumberWithDecimal(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);

    // Allow digits and decimal point
    if (!/^[0-9.]$/.test(inputChar)) {
      event.preventDefault();
      return;
    }

    // Disallow more than one decimal point
    const input = event.target as HTMLInputElement;
    if (inputChar === '.' && input.value.includes('.')) {
      event.preventDefault();
    }
  }

  blockPaste(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    if (!/^\d*\.?\d*$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const inputChar = String.fromCharCode(event.keyCode || event.which);
  
    // Allow only digits (0-9)
    if (!/^[0-9]$/.test(inputChar)) {
      event.preventDefault();
    }
  }
  
  blockPasteNumbers(event: ClipboardEvent): void {
    const pastedInput: string = event.clipboardData?.getData('text') || '';
    
    // Allow only whole numbers (no decimals)
    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }
  

  getPropertyById() {
    this.accommodationService.getPropertyById(Number(this.accommodationId)).subscribe({
      next: (data) => {
        this.accommodationData = data;
        this.initForm();
      },
      error: (error) => {
        console.error(error, 'Error');
      },
    });
  }

  async onSubmit() {
    if (this.newAccommodationForm.invalid) {
      this.newAccommodationForm.markAllAsTouched();
      return;
    }
    const formValue = this.newAccommodationForm.value;
    const branchId = localStorage.getItem('selectedBranchId');

    const createPropertyInput = {
      propertyName: formValue.propertyName,
      address: formValue.address,
      annualRent: parseFloat(formValue.annualRent) || 0,
      vacancies: Number(formValue.vacancies),
      branchId: Number(branchId),
      // status: 1,
    };

    if (this.accommodationId) {
      this.accommodationService
        .updateProperty(Number(this.accommodationId), createPropertyInput,)
        .subscribe({
          next: async (data) => {
            this.toastr.success('Property updated successfully', 'Success');
            this.onCancel();
          },
          error: (error) => this.toastr.error(error, 'Error'),
        });
    } else {
      this.accommodationService.createProperty(createPropertyInput).subscribe({
        next: async (data) => {
          this.toastr.success('Property created successfully', 'Success');
          this.onCancel();
        },
        error: (error) => this.toastr.error(error, 'Error'),
      });
    }
  }

  onCancel() {
    this.router.navigate([`/accommodation`]);
  }
}
